"use client";

import { useCallback, useEffect, useState } from "react";
import { useRecoilState } from "recoil";
import { KeyedMutator } from "swr";

import { TContractDetail } from "builder/contract/buildSendContract";
import { Button, RequestSignConfirmModal } from "components";
import { EXCEPTION } from "constants/modalTemplate";
import { onlineContractStep } from "constants/onlineContract";
import { useLayoutData } from "contexts/LayoutContext";
import { GetContractPdfDocumentResult, LessorGetContractDetailQuery } from "graphql/type";
import { useModal } from "hooks/useModal";
import { fileContractAtom } from "store/activeTabAtom";
import { onDownloadByFileUrl } from "utils/download.util";

export const ConfirmRequestSign = ({
  onRequestSignContract,
  openCreateContractTab,
  getContractPdfDocument,
  isCreatedContract,
  confirmAndRequestStampAt,
}: {
  onRequestSignContract: () => Promise<void>;
  openCreateContractTab: () => void;
  getContractPdfDocument: ({
    contractDetailId,
  }: {
    contractDetailId: string;
  }) => Promise<GetContractPdfDocumentResult | undefined>;
  isCreatedContract: boolean;
  confirmAndRequestStampAt: string;
}) => {
  const { contractDetail, isContractCancelled, mutateContractDetail } = useLayoutData<{
    contractDetail?: TContractDetail | null;
    isContractCancelled?: boolean;
    mutateContractDetail?: KeyedMutator<LessorGetContractDetailQuery>;
  }>();
  const [isConfirmRequestSignModalOpen, setIsConfirmRequestSignModalOpen] = useState(false);
  const [isFetchingPdf, setIsFetchingPdf] = useState(false);
  const [fileContract, setFileContract] = useRecoilState(fileContractAtom);
  const { openErrorModal } = useModal();

  const {
    name: nameInfo,
    email: emailInfo,
    signatory_name: signatoryNameInfo,
    signatory_email: signatoryEmailInfo,
    signatory_type: signatoryTypeInfo,
  } = contractDetail?.applicationLessorSignatoryInfo ?? {};
  const isContractHolder = signatoryTypeInfo === 0;
  const { isCorporate, cContractor, name } = contractDetail?.application?.applicationContractor ?? {};

  const fetchContractPdf = useCallback(async () => {
    if (isFetchingPdf || !contractDetail?.id) return;

    try {
      setIsFetchingPdf(true);
      const pdf = await getContractPdfDocument({ contractDetailId: contractDetail.id });
      setFileContract(pdf?.generated?.fileUrl || "");
      return pdf;
    } catch (error) {
      console.error("Error fetching contract PDF:", error);
    } finally {
      setIsFetchingPdf(false);
    }
  }, [isFetchingPdf, contractDetail?.id, getContractPdfDocument, setFileContract]);

  const onClickDownload = async () => {
    if (!fileContract) return;
    const fileName = (contractDetail?.application?.applicationContractor?.name || "") + "様_駐車場賃貸管理契約書.pdf";

    await onDownloadByFileUrl({
      filename: fileName,
      fileUrl: fileContract,
      isPdf: true,
    });
  };

  const handleSubmitRequestSign = async () => {
    try {
      await onRequestSignContract();
      await mutateContractDetail?.();
      setIsConfirmRequestSignModalOpen(false);
    } catch (error) {
      openErrorModal(EXCEPTION);
    }
  };

  useEffect(() => {
    fetchContractPdf();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    let intervalId: NodeJS.Timeout | undefined;

    const shouldFetch =
      !fileContract &&
      !!contractDetail?.id &&
      contractDetail.contractConfirmationStep > onlineContractStep.CREATE_CONTRACT;

    if (shouldFetch) {
      intervalId = setInterval(async () => {
        const pdf = await fetchContractPdf();
        if (pdf) {
          mutateContractDetail?.();
          clearInterval(intervalId!);
        }
      }, 5000);
    }

    return () => {
      if (intervalId) clearInterval(intervalId);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fileContract, contractDetail?.id, contractDetail?.contractConfirmationStep, fetchContractPdf]);

  return (
    <div className="flex flex-col bg-white px-10 py-6">
      <h1 className="flex justify-between text-2xl leading-[1.25]">契約書の確認</h1>
      <p className="text-m my-4">
        契約書をダウンロードして確認し、「契約書の確認と押印を依頼」をクリックしてください。
      </p>

      <div className="w-[796px] border border-border px-8 py-[23px]">
        {fileContract ? (
          <div className="flex w-[732px] items-center justify-center rounded-sm bg-primary/5 py-[48px]">
            <div className="mr-10 flex h-[200px] w-[200px] flex-col items-center justify-center rounded-full bg-white">
              <img src="/svg/filePdf.svg" alt="filePdf" />
              <span className="mt-2 font-bold ">作成完了</span>
            </div>

            <div>
              <p>契約書の作成が完了しました。</p>
              <p>ダウンロードして確認ください。</p>
              <div className="mt-1 flex">
                <div>
                  <p className="text-s text-gray">
                    ※変更・修正が必要な場合は「
                    <span className="cursor-pointer text-primary" onClick={openCreateContractTab}>
                      契約書の作成
                    </span>
                    」に
                  </p>
                  {/* eslint-disable-next-line no-irregular-whitespace */}
                  <p className="mb-4 text-s text-gray">　戻って再度作成してください</p>
                </div>
              </div>

              <Button
                style="outline"
                width="w-[192px]"
                onClick={onClickDownload}
                text="契約書をダウンロード"
                isDisabled={isContractCancelled}
              />
            </div>
          </div>
        ) : (
          <div className="flex w-[732px] items-center justify-center rounded-sm bg-default py-[48px]">
            <div className="mr-10 flex h-[200px] w-[200px] flex-col items-center justify-center rounded-full bg-white">
              <img src="/svg/noFilePdf.svg" alt="noFilePdf" />
              <span className="mt-2 font-bold text-disabled">作成中</span>
            </div>

            <div>
              <p>
                契約書の作成には
                <br />
                数十秒かかる場合があります。
                <br />
                完了までお待ちください。
              </p>
            </div>
          </div>
        )}

        <div className="mt-6 w-[732px] border border-border px-6 py-[23px]">
          <p className="h-[21px] text-s">契約書の確認と押印の依頼先</p>
          <p className="mt-4 text-s text-gray">
            <span className="mr-2 text-16px font-bold text-black">
              {isContractHolder ? nameInfo : signatoryNameInfo}
            </span>
            {isContractHolder ? emailInfo : signatoryEmailInfo}
          </p>
          <p className="mt-2 text-s text-gray">
            <span className="mr-2 text-16px font-bold text-black">{isCorporate ? cContractor?.name : name}</span>
            {contractDetail?.application?.email}
          </p>
        </div>

        <div className="flex items-center justify-end gap-2.5 pt-6">
          {confirmAndRequestStampAt && <span className="text-s text-gray">依頼済み {confirmAndRequestStampAt}</span>}
          <Button
            isLoading={false}
            isDisabled={isCreatedContract || !fileContract || isContractCancelled}
            width="w-[224px]"
            type="submit"
            text="契約書の確認と押印を依頼"
            onClick={() => setIsConfirmRequestSignModalOpen(true)}
          />
        </div>
      </div>

      <RequestSignConfirmModal
        isOpen={isConfirmRequestSignModalOpen}
        onClose={() => setIsConfirmRequestSignModalOpen(false)}
        onConfirm={handleSubmitRequestSign}
      />
    </div>
  );
};
